import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Copy, Check } from 'lucide-react';

interface Props {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: { name: string; value?: string; description?: string; service?: string }) => Promise<void>;
    onGenerate: () => Promise<string>;
    editMode?: { name: string; description?: string; service?: string };
    scopeLabel: string;
}

export const CreateSecretDialog: React.FC<Props> = ({ open, onClose, onSubmit, onGenerate, editMode, scopeLabel }) => {
    const [name, setName] = useState(editMode?.name ?? '');
    const [value, setValue] = useState('');
    const [description, setDescription] = useState(editMode?.description ?? '');
    const [service, setService] = useState(editMode?.service ?? '');
    const [loading, setLoading] = useState(false);
    const [copiedValue, setCopiedValue] = useState(false);
    const [editingValue, setEditingValue] = useState(false);

    const copyValue = async () => {
        if (!value) return;
        await navigator.clipboard.writeText(value);
        setCopiedValue(true);
        setTimeout(() => setCopiedValue(false), 1500);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name.trim() || (!editMode && !value.trim())) return;
        setLoading(true);
        try {
            await onSubmit({
                name: name.trim(),
                ...(value ? { value } : {}),
                ...(description.trim() ? { description: description.trim() } : {}),
                ...(service.trim() ? { service: service.trim() } : {}),
            });
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        const generated = await onGenerate();
        setValue(generated);
        if (editMode) setEditingValue(true);
    };

    return (
        <Dialog open={open} onOpenChange={val => !val && onClose()}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>{editMode ? `Update ${scopeLabel} Secret` : `Add ${scopeLabel} Secret`}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="secret-name">Secret Name</Label>
                        <Input
                            id="secret-name"
                            name="secret-name"
                            autoComplete="off"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="e.g. STRIPE_API_KEY"
                            disabled={!!editMode}
                            autoFocus={!editMode}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="secret-value">Value</Label>
                            <div className="flex items-center gap-1">
                                {value && (
                                    <Button type="button" variant="ghost" size="sm" className="h-6 text-xs gap-1" onClick={copyValue}>
                                        {copiedValue ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                        {copiedValue ? 'Copied' : 'Copy'}
                                    </Button>
                                )}
                                <Button type="button" variant="ghost" size="sm" className="h-6 text-xs" onClick={handleGenerate}>
                                    Generate
                                </Button>
                            </div>
                        </div>
                        {editMode && !editingValue ? (
                            <button
                                type="button"
                                onClick={() => setEditingValue(true)}
                                className="flex items-center h-[78px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-left group hover:border-neutral-400 dark:hover:border-neutral-500 transition-colors"
                            >
                                <span className="flex-1 text-neutral-400 dark:text-neutral-500 font-mono tracking-wider">••••••••••••</span>
                                <span className="text-xs text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity">
                                    Click to edit
                                </span>
                            </button>
                        ) : (
                            <Textarea
                                id="secret-value"
                                value={value}
                                onChange={e => setValue(e.target.value)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
                                        event.preventDefault();
                                        handleSubmit(event);
                                    }
                                }}
                                placeholder="Enter secret value..."
                                className="font-mono text-sm"
                                rows={3}
                                autoFocus={editMode && editingValue}
                            />
                        )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="secret-service">Service (optional)</Label>
                        <Input
                            id="secret-service"
                            value={service}
                            onChange={e => setService(e.target.value)}
                            placeholder="e.g. Stripe, AWS"
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="secret-desc">Description (optional)</Label>
                        <Input
                            id="secret-desc"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="What is this secret for?"
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading || !name.trim() || (!editMode && !value.trim())}>
                            {loading ? 'Saving...' : editMode ? 'Update' : 'Create'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
